import React from 'react';
import { z } from 'zod';
import type { BlockDefinition } from '../../types';
import { KHead, KIcon, KImage } from './shared';
import { LeadFormFields } from '../leadForm';

/**
 * Kathrefits AI corporate-website blocks (`k*`). Markup only — ALL styling is in
 * the hand-editable stylesheet `apps/web/public/styles/kathreftis.css` (served at
 * /styles/kathreftis.css). Components emit semantic classes; no inline styles, no
 * styled-components, no third-party CSS/JS/fonts. Content is prop-driven with the
 * handoff copy as defaults, so a page is just an ordered list of block types.
 */

const linkSchema = z.object({ label: z.string(), href: z.string().default('#'), newTab: z.boolean().default(false) });
const footerLink = z.preprocess(
  (v) => (typeof v === 'string' ? { label: v, href: '#', newTab: false } : v),
  linkSchema,
);
const nl = (label: string, href = '#') => ({ label, href, newTab: false });
const ext = (n?: boolean) => (n ? { target: '_blank' as const, rel: 'noopener noreferrer' } : {});

const PRODUCTS = [
  {
    name: 'Cymmetri', tag: 'Converged identity platform', site: 'cymmetri.com', href: 'https://cymmetri.com',
    blurb: 'The converged identity platform — SSO, MFA, identity governance and full user-lifecycle management for every human and non-human identity, in one console.',
    long: 'Cymmetri is our flagship converged identity platform: single sign-on with adaptive MFA, identity governance and access certification, and joiner-mover-leaver lifecycle automation — governing employees, partners, customers, service accounts and agents from one place, across exchanges, banks and NBFCs.',
    points: ['SSO & MFA', 'Identity governance', 'Lifecycle management', 'Adaptive access'],
    detail: [
      { t: 'SSO + adaptive MFA', b: 'One sign-in, risk-scored on context and device.' },
      { t: 'Identity governance', b: 'Access reviews, SoD and audit-ready certification.' },
      { t: 'Lifecycle management', b: 'Joiner-mover-leaver automation from HR truth.' },
      { t: 'Self-service', b: 'Password and access requests without the helpdesk.' },
    ],
    image: '',
  },
  {
    name: 'Bastion', tag: 'PAM for human & non-human identities', site: 'thebastion.io', href: 'https://thebastion.io',
    blurb: 'Privileged access that no longer stops at people — humans, service accounts and AI agents each get a governed privileged identity with a named owner.',
    long: 'Bastion is PAM for the whole privileged estate — human and non-human. Every service account, bot and AI agent is onboarded like a colleague: a lifecycle, an accountable owner, scoped just-in-time capabilities, spend limits, and inspection of every request before it reaches a model.',
    points: ['Just-in-time access', 'Non-human identity', 'LLM data protection', 'Break-glass'],
    detail: [
      { t: 'Non-human identity', b: 'Service accounts, bots and agents — each with an owner.' },
      { t: 'Just-in-time rights', b: 'Scoped to the moment; grants expire themselves.' },
      { t: 'Data protection', b: 'Secrets and PII stopped before they leave a session.' },
      { t: 'Risk containment', b: 'Behavioural scoring auto-pauses off-rails agents.' },
    ],
    image: '',
  },
  {
    name: 'Conflux ITDR', tag: 'Identity Threat Detection & Response', site: 'cymmetri.com', href: 'https://cymmetri.com',
    blurb: 'Identity Threat Detection & Response — a unified identity graph with progressive risk scoring, attack-path detection and policy-gated response across human and non-human identities.',
    long: 'Conflux ITDR watches the whole identity estate. Two independent pipelines — batch reconnaissance and high-volume event streams — feed one identity graph, so you see risky access, exposed non-human identities and live attack paths, then respond through policy-gated, fully-audited actions.',
    points: ['Identity graph', 'Progressive risk', 'Attack-path detection', 'Policy-gated response'],
    detail: [
      { t: 'Unified identity graph', b: 'Every human, service account and agent in one model.' },
      { t: 'Progressive risk', b: 'Continuous scoring from recon and real-time events.' },
      { t: 'Attack-path detection', b: 'Surface how an attacker could chain access.' },
      { t: 'Policy-gated response', b: 'Contain threats through audited, approved actions.' },
    ],
    image: '',
  },
  {
    name: 'IntelliAuth', tag: 'Customer authentication (CIAM)', site: 'intelliauth.io', href: 'https://intelliauth.io',
    blurb: 'Enterprise CIAM with adaptive MFA and consent — build login, registration and MFA flows on a visual canvas, with multi-tenant isolation and data residency.',
    long: 'IntelliAuth is the developer-facing half of the fabric: CIAM with adaptive MFA and consent. Ship a production login in days with a drag-and-drop flow builder, isolated tenants with their own branding, SAML and OIDC federation, JavaScript actions, and regional deployment for data-residency mandates.',
    points: ['Visual flow builder', 'Adaptive MFA & consent', 'Multi-tenant', 'Data residency'],
    detail: [
      { t: 'Flow builder', b: 'Design auth journeys without shipping code.' },
      { t: 'Adaptive MFA & consent', b: 'Step-up authentication and consent capture.' },
      { t: 'Multi-tenant', b: 'Isolated environments, separate branding per tenant.' },
      { t: 'Developer first', b: 'SDKs, APIs, webhooks and reference docs.' },
    ],
    image: '',
  },
  {
    name: 'Cymmetri Learn', tag: 'Enablement & LMS', site: 'learn.cymmetri.com', href: 'https://learn.cymmetri.com',
    blurb: 'The learning platform behind the stack — certification for admins and partners, plus security-awareness tracks for the workforce.',
    long: 'Cymmetri Learn carries the training layer: role-based certification paths for administrators and channel partners, security-awareness modules for end users, and completion reporting that plugs back into governance evidence.',
    points: ['Admin certification', 'Partner enablement', 'Awareness tracks', 'Reporting'],
    detail: [
      { t: 'Certification paths', b: 'Role-based tracks for admins and implementers.' },
      { t: 'Partner enablement', b: 'Resellers ramp to delivery-ready on their own.' },
      { t: 'Awareness training', b: 'Phishing and hygiene modules for the workforce.' },
      { t: 'Evidence', b: 'Completion reporting usable in audits.' },
    ],
    image: '',
  },
];
const productSchema = z.object({
  name: z.string(), tag: z.string(), site: z.string(), href: z.string().default('#'),
  blurb: z.string().default(''), long: z.string().default(''),
  points: z.array(z.string()).default([]),
  detail: z.array(z.object({ t: z.string(), b: z.string() })).default([]),
  image: z.string().default(''),
});
const fieldSchema = z.array(z.object({ ph: z.string() })).default([
  { ph: 'Your name' }, { ph: 'Work email' }, { ph: 'Company' }, { ph: 'Phone' },
]);

// ── 1. Nav (sticky header + mobile off-canvas drawer) ────────────────────────
export const kNavSchema = z.object({
  wordmark: z.string().default('Kathrefits AI'),
  active: z.enum(['home', 'products', 'about', 'contact']).default('home'),
  topInfo: z.array(z.object({ icon: z.string(), label: z.string(), value: z.string() })).default([
    { icon: 'ph ph-map-pin', label: 'Head office', value: 'Mumbai, India' },
    { icon: 'ph ph-envelope-simple', label: 'Send us email', value: 'sales@cymmetri.com' },
    { icon: 'ph ph-clock', label: 'Support hours', value: '24/7' },
  ]),
  links: z.array(z.object({ key: z.string(), label: z.string(), href: z.string() })).default([
    { key: 'home', label: 'Home', href: '/' },
    { key: 'products', label: 'Products', href: '/products' },
    { key: 'about', label: 'About Us', href: '/about' },
    { key: 'contact', label: 'Contact', href: '/contact' },
  ]),
  talkLabel: z.string().default('Talk to us'),
  talkHref: z.string().default('/contact'),
  talkCalendly: z.boolean().default(false), // open the tenant's Calendly instead of href
});
function KNav(p: z.infer<typeof kNavSchema>) {
  const info = (t: { icon: string; label: string; value: string }, i: number) => (
    <div className="k-info" key={i}>
      <KIcon name={t.icon} size={20} />
      <span className="k-info-v"><span className="k-info-l">{t.label}</span>{t.value}</span>
    </div>
  );
  return (
    <div className="k-root k-nav">
      <KHead />
      <input id="k-nav" className="k-toggle" type="checkbox" aria-label="Toggle navigation menu" />
      <header className="k-header">
        <div className="k-util">
          <div className="k-wrap k-util-row">
            <a className="k-brand" href="/"><img className="k-site-logo" src="/kathreftis/images/kathreftis-logo.png" alt={p.wordmark} /></a>
            <div className="k-info-inline">{p.topInfo.map(info)}</div>
            <label className="k-burger" htmlFor="k-nav" aria-label="Open menu">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" /></svg>
            </label>
          </div>
        </div>
        <div className="k-navbar">
          <div className="k-wrap k-navrow">
            <nav className="k-links">
              {p.links.map((l) => (<a key={l.key} href={l.href} className={l.key === p.active ? 'is-active' : undefined}>{l.label}</a>))}
            </nav>
            <a href={p.talkCalendly ? '#calendly' : p.talkHref} className="k-btn k-btn--sm k-talk">{p.talkLabel}</a>
          </div>
        </div>
      </header>
      <div className="k-spacer" aria-hidden="true" />
      <label className="k-backdrop" htmlFor="k-nav" aria-label="Close menu" />
      <aside className="k-drawer">
        <div className="k-drawer__head">
          <span className="k-brand"><img className="k-site-logo" src="/kathreftis/images/kathreftis-logo.png" alt={p.wordmark} /></span>
          <label className="k-drawer__close" htmlFor="k-nav" aria-label="Close menu">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="6" y1="6" x2="18" y2="18" /><line x1="18" y1="6" x2="6" y2="18" /></svg>
          </label>
        </div>
        <nav className="k-drawer__links">
          {p.links.map((l) => (<a key={l.key} href={l.href} className={l.key === p.active ? 'is-active' : undefined}>{l.label}</a>))}
        </nav>
        <a href={p.talkCalendly ? '#calendly' : p.talkHref} className="k-btn k-drawer__talk">{p.talkLabel}</a>
        <div className="k-drawer__info">{p.topInfo.map(info)}</div>
      </aside>
    </div>
  );
}
export const kNavBlock: BlockDefinition = { type: 'kNav', schema: kNavSchema, Renderer: KNav };

// ── 2. Hero ──────────────────────────────────────────────────────────────────
export const kHeroSchema = z.object({
  eyebrow: z.string().default('Identity security for the age of AI agents'),
  headline: z.string().default('Innovative And Governed Identity'),
  body: z.string().default('Kathrefits AI Pvt Ltd builds the identity fabric behind Cymmetri, Bastion, Conflux ITDR, IntelliAuth and Cymmetri Learn — governing every employee, customer, admin, service account and autonomous agent.'),
  ctaLabel: z.string().default('More Details'),
  ctaHref: z.string().default('/products'),
  ctaCalendly: z.boolean().default(false),
  email: z.string().default('sales@cymmetri.com'),
  image: z.string().default(''),
});
function KHero(p: z.infer<typeof kHeroSchema>) {
  return (
    <section className="k-root k-hero">
      <div className="k-hero__bg" style={p.image ? { backgroundImage: `url(${p.image})` } : undefined} />
      <div className="k-hero__overlay" />
      <div className="k-hero__inner">
        <p className="k-hero__eyebrow">{p.eyebrow}</p>
        <h1 className="k-h k-hero__title">{p.headline}</h1>
        <p className="k-hero__body">{p.body}</p>
        <div className="k-hero__cta">
          <a href={p.ctaCalendly ? '#calendly' : p.ctaHref} className="k-btn">{p.ctaLabel} <KIcon name="ph ph-arrow-right" size={16} /></a>
          <span className="k-hero__email">Or email <a href={`mailto:${p.email}`}>{p.email}</a></span>
        </div>
      </div>
    </section>
  );
}
export const kHeroBlock: BlockDefinition = { type: 'kHero', schema: kHeroSchema, Renderer: KHero };

// ── 3. Services ──────────────────────────────────────────────────────────────
export const kServicesSchema = z.object({
  kicker: z.string().default('Kathrefits AI Solutions'),
  heading: z.string().default('Solutions And Focus Areas'),
  body: z.string().default('Employees, customers, contractors, service accounts, machines and autonomous agents — governed under one model, with one audit trail.'),
  ctaHref: z.string().default('/products'),
  items: z.array(z.object({ icon: z.string(), title: z.string(), body: z.string() })).default([
    { icon: 'ph ph-identification-badge', title: 'Workforce Identity', body: 'Joiner-mover-leaver automation from HR truth, with governance and certification behind it.' },
    { icon: 'ph ph-key', title: 'Privileged Access', body: 'Vaulted credentials, session control and just-in-time rights instead of standing admin.' },
    { icon: 'ph ph-fingerprint', title: 'Customer Authentication', body: 'CIAM flows, passwordless sign-in and adaptive MFA your product team can ship itself.' },
    { icon: 'ph ph-robot', title: 'AI Agent Governance', body: 'Every agent an identity with an owner, a budget, scoped access and a retirement date.' },
    { icon: 'ph ph-shield-check', title: 'Compliance Evidence', body: 'Access reviews, separation of duties and audit trails that survive the regulator.' },
    { icon: 'ph ph-graduation-cap', title: 'Enablement & Training', body: 'Certification for your admins and partners, awareness tracks for everyone else.' },
  ]),
});
function KServices(p: z.infer<typeof kServicesSchema>) {
  return (
    <section className="k-root k-band k-band--tint1 k-pt-76 k-pb-70">
      <div className="k-wrap">
        <div className="k-center">
          <p className="k-kicker">{p.kicker}</p>
          <h2 className="k-h k-fs-34 k-mb-14">{p.heading}</h2>
          <p className="k-body">{p.body}</p>
        </div>
        <div className="k-grid3">
          {p.items.map((s, i) => (
            <div className="k-card" key={i}>
              <KIcon name={s.icon} size={34} className="k-card__icon" />
              <h3 className="k-h k-card__title">{s.title}</h3>
              <p className="k-body k-card__body">{s.body}</p>
              <a href={p.ctaHref} className="k-arrow" aria-label="Read more"><KIcon name="ph ph-arrow-right" size={14} /></a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
export const kServicesBlock: BlockDefinition = { type: 'kServices', schema: kServicesSchema, Renderer: KServices };

// ── 4. CTA strip ──────────────────────────────────────────────────────────────
export const kCtaStripSchema = z.object({
  text: z.string().default("We're ready to review where your standing access sits."),
  linkLabel: z.string().default('Get the assessment'),
  linkHref: z.string().default('/contact'),
  email: z.string().default('sales@cymmetri.com'),
});
function KCtaStrip(p: z.infer<typeof kCtaStripSchema>) {
  return (
    <section className="k-root k-band k-band--bt k-band--bb k-pt-26 k-pb-26">
      <div className="k-wrap k-ctastrip">
        {p.text} <a href={p.linkHref}>{p.linkLabel}</a> or write to <strong>{p.email}</strong>
      </div>
    </section>
  );
}
export const kCtaStripBlock: BlockDefinition = { type: 'kCtaStrip', schema: kCtaStripSchema, Renderer: KCtaStrip };

// ── 5. Statement band ─────────────────────────────────────────────────────────
export const kStatementSchema = z.object({
  text: z.string().default('Our identity platforms move your estate towards zero standing access.'),
});
function KStatement(p: z.infer<typeof kStatementSchema>) {
  return (
    <section className="k-root k-band k-band--navy k-pt-70 k-pb-70">
      <div className="k-wrap k-statement">
        <span className="k-statement__play"><KIcon name="ph-fill ph-play" size={26} /></span>
        <h2 className="k-h k-statement__h">{p.text}</h2>
      </div>
    </section>
  );
}
export const kStatementBlock: BlockDefinition = { type: 'kStatement', schema: kStatementSchema, Renderer: KStatement };

// ── 6. About / mission ────────────────────────────────────────────────────────
export const kAboutMissionSchema = z.object({
  kicker: z.string().default('Kathrefits AI — a product company'),
  heading: z.string().default('Let Us Be Your Next Identity Partner'),
  body: z.string().default('Research, engineering, deployment and escalation all sit under one roof. Our engineers run the rollout, which is why platform migrations here are measured in weeks, not quarters.'),
  miniCards: z.array(z.object({ icon: z.string(), title: z.string() })).default([
    { icon: 'ph ph-cpu', title: 'Product engineering' },
    { icon: 'ph ph-lock-key', title: 'Security research' },
    { icon: 'ph ph-users-three', title: 'Deployment teams' },
  ]),
  missionHeading: z.string().default('Company Mission And Vision'),
  missionBody: z.string().default('To make every identity in an enterprise — human, machine or agent — accountable to a named owner, with the least access it needs and an audit trail a regulator will accept.'),
  checks: z.array(z.string()).default([
    'Converged, not stitched — one model for workforce, customer and privileged identity',
    'Indian product with data residency and local escalation paths',
    'Agent identity in production today, not on a roadmap slide',
    'Rollouts run by the engineers who built the platform',
    'Partner-led delivery, certified through Cymmetri Learn',
  ]),
  imageLeft: z.string().default(''), imageRight: z.string().default(''), imageMain: z.string().default(''),
});
function KAboutMission(p: z.infer<typeof kAboutMissionSchema>) {
  return (
    <section className="k-root k-band k-pt-80 k-pb-80">
      <div className="k-wrap">
        <div className="k-two">
          <div>
            <div className="k-collage">
              <KImage src={p.imageLeft} alt="Team photo" className="k-slot--h220" />
              <KImage src={p.imageRight} alt="Office photo" className="k-slot--h220 k-slot--mt28" />
            </div>
            <p className="k-kicker">{p.kicker}</p>
            <h2 className="k-h k-fs-32 k-mb-14">{p.heading}</h2>
            <p className="k-body">{p.body}</p>
            <div className="k-minis">
              {p.miniCards.map((m, i) => (
                <div className="k-mini" key={i}>
                  <KIcon name={m.icon} size={28} className="k-mini__icon" />
                  <div className="k-mini__label">{m.title}</div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <KImage src={p.imageMain} alt="Customer meeting photo" className="k-slot--h300" />
            <h3 className="k-h k-fs-24 k-mission__head">{p.missionHeading}</h3>
            <p className="k-body">{p.missionBody}</p>
            <div className="k-checks">
              {p.checks.map((c, i) => (
                <div className="k-check" key={i}><KIcon name="ph ph-check" size={15} /><span>{c}</span></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
export const kAboutMissionBlock: BlockDefinition = { type: 'kAboutMission', schema: kAboutMissionSchema, Renderer: KAboutMission };

// ── 7. Stats ──────────────────────────────────────────────────────────────────
export const kStatsSchema = z.object({
  heading: z.string().default('The Numbers Speak'),
  stats: z.array(z.object({ value: z.string(), tag: z.string(), label: z.string() })).default([
    { value: '60%', tag: 'Fewer breaches', label: 'Reported reduction in incidents after rollout' },
    { value: '50M+', tag: 'Every month', label: 'Authentications served across the platforms' },
    { value: '99.9%', tag: 'Uptime SLA', label: 'On our managed deployments' },
    { value: '5', tag: 'Products', label: 'Built, owned and supported in-house' },
  ]),
});
function KStats(p: z.infer<typeof kStatsSchema>) {
  return (
    <section className="k-root k-band k-band--navy k-pt-62 k-pb-62">
      <div className="k-wrap">
        <h2 className="k-h k-stats__h">{p.heading}</h2>
        <div className="k-grid4">
          {p.stats.map((s, i) => (
            <div className="k-stat" key={i}>
              <div className="k-stat__value">{s.value}</div>
              <div className="k-stat__tag">{s.tag}</div>
              <div className="k-stat__label">{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
export const kStatsBlock: BlockDefinition = { type: 'kStats', schema: kStatsSchema, Renderer: KStats };

// ── 8. Products (home rows) ───────────────────────────────────────────────────
export const kProductsSchema = z.object({
  kicker: z.string().default('Our products'),
  heading: z.string().default('Identity Platforms & Projects'),
  body: z.string().default('Four products, each shipping on its own — together they cover the whole estate.'),
  products: z.array(productSchema).default(PRODUCTS),
});
function KProducts(p: z.infer<typeof kProductsSchema>) {
  return (
    <section className="k-root k-band k-pt-78 k-pb-40">
      <div className="k-wrap">
        <div className="k-center k-center--680">
          <p className="k-kicker">{p.kicker}</p>
          <h2 className="k-h k-fs-34 k-mb-14">{p.heading}</h2>
          <p className="k-body">{p.body}</p>
        </div>
        <div className="k-prodlist">
          {p.products.map((pr, i) => (
            <article className="k-prod k-two k-two--flush" key={i}>
              <div className="k-prod__body">
                <h3 className="k-h k-prod__name">{pr.name}</h3>
                <div className="k-prod__tag">{pr.tag}</div>
                <p className="k-body">{pr.blurb}</p>
                <div className="k-chips">{pr.points.map((pt, j) => (<span className="k-chip" key={j}>{pt}</span>))}</div>
                <a href={pr.href} target="_blank" rel="noreferrer" className="k-prod__link">View {pr.site} <KIcon name="ph ph-arrow-up-right" size={14} /></a>
              </div>
              <KImage src={pr.image} alt={`${pr.name} product shot`} className="k-slot--h290" />
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
export const kProductsBlock: BlockDefinition = { type: 'kProducts', schema: kProductsSchema, Renderer: KProducts };

// ── 9. Logo strip ─────────────────────────────────────────────────────────────
export const kLogosSchema = z.object({
  logos: z.array(z.string()).default(['BSE Ltd.', 'Aditya Birla Finance', 'SBI Mutual Fund', 'Bank of India', 'Mahindra Finance']),
});
function KLogos(p: z.infer<typeof kLogosSchema>) {
  return (
    <section className="k-root k-band k-band--bb k-pt-30 k-pb-46">
      <div className="k-wrap k-logos">
        {p.logos.map((l, i) => (<span className="k-logo" key={i}>{l}</span>))}
      </div>
    </section>
  );
}
export const kLogosBlock: BlockDefinition = { type: 'kLogos', schema: kLogosSchema, Renderer: KLogos };

// ── 10. Industries ────────────────────────────────────────────────────────────
export const kIndustriesSchema = z.object({
  kicker: z.string().default('Industries we serve'),
  heading: z.string().default('Where Kathrefits AI Is Already Inside'),
  body: z.string().default('Regulated estates with heterogeneous IT and hard audit requirements.'),
  items: z.array(z.object({ icon: z.string(), name: z.string() })).default([
    { icon: 'ph ph-bank', name: 'Banking & NBFC' },
    { icon: 'ph ph-chart-line-up', name: 'Capital markets' },
    { icon: 'ph ph-buildings', name: 'Public sector' },
    { icon: 'ph ph-factory', name: 'Manufacturing' },
    { icon: 'ph ph-code', name: 'ITES' },
    { icon: 'ph ph-shield', name: 'Defence' },
  ]),
});
function KIndustries(p: z.infer<typeof kIndustriesSchema>) {
  return (
    <section className="k-root k-band k-band--tint2 k-pt-76 k-pb-76">
      <div className="k-wrap">
        <div className="k-two k-two--aside">
          <div>
            <p className="k-kicker">{p.kicker}</p>
            <h2 className="k-h k-fs-30 k-mb-14">{p.heading}</h2>
            <p className="k-body">{p.body}</p>
          </div>
          <div className="k-tiles">
            {p.items.map((it, i) => (
              <div className="k-tile" key={i}>
                <KIcon name={it.icon} size={26} className="k-tile__icon" />
                <span className="k-tile__label">{it.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
export const kIndustriesBlock: BlockDefinition = { type: 'kIndustries', schema: kIndustriesSchema, Renderer: KIndustries };

// ── 11. Testimonials ──────────────────────────────────────────────────────────
export const kTestimonialsSchema = z.object({
  kicker: z.string().default('Read the testimonials'),
  heading: z.string().default('What Clients Say About Us'),
  quotes: z.array(z.object({ text: z.string(), who: z.string(), org: z.string(), avatar: z.string().default('') })).default([
    { text: 'With digitalization came the challenge of managing access to the application landscape — unified access and password fatigue were emerging as challenges. We implemented Cymmetri as our IAM solution.', who: 'Group CISO', org: 'BSE Ltd.', avatar: '' },
    { text: 'As we expanded into different finance verticals, Identity Management at a group level became an aspiration. Cymmetri is easy to implement and manage, and gave us a single-window approach for a vast number of employees.', who: 'Lead Info Security', org: 'Aditya Birla Finance Limited', avatar: '' },
  ]),
});
function KTestimonials(p: z.infer<typeof kTestimonialsSchema>) {
  return (
    <section className="k-root k-band k-pt-76 k-pb-76">
      <div className="k-wrap">
        <div className="k-center k-center--640">
          <p className="k-kicker">{p.kicker}</p>
          <h2 className="k-h k-fs-34">{p.heading}</h2>
        </div>
        <div className="k-two">
          {p.quotes.map((q, i) => (
            <div className="k-quote" key={i}>
              <KIcon name="ph-fill ph-quotes" size={26} className="k-quote__mark" />
              <p className="k-quote__text">{q.text}</p>
              <div className="k-quote__who">
                <span className="k-quote__avatar"><KImage src={q.avatar} alt={q.who} className="k-slot--h56 k-slot--round" /></span>
                <span className="k-quote__meta"><span className="k-quote__name">{q.who}</span><span className="k-quote__org">{q.org}</span></span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
export const kTestimonialsBlock: BlockDefinition = { type: 'kTestimonials', schema: kTestimonialsSchema, Renderer: KTestimonials };

// ── 12. Contact / appointment band (home) ─────────────────────────────────────
export const kContactSchema = z.object({
  eyebrow: z.string().default('Get in touch with us today'),
  heading: z.string().default('Need Our Platforms? Book Your Assessment'),
  body: z.string().default("Tell us what you run today. We'll tell you which of the four products you actually need — and which you don't."),
  email: z.string().default('sales@cymmetri.com'),
  partnerLabel: z.string().default('Partner with us'),
  partnerHref: z.string().default('https://cymmetri.com/partner'),
  fields: fieldSchema,
  submitLabel: z.string().default('Make a request'),
});
// Kathreftis contact forms use the SHARED cross-tenant LeadFormFields component
// (packages/blocks/src/blocks/leadForm.tsx) for a consistent form on every tenant.
function KContact(p: z.infer<typeof kContactSchema>) {
  return (
    <section className="k-root k-band k-band--navy k-pt-70 k-pb-70">
      <div className="k-wrap">
        <div className="k-two">
          <div>
            <p className="k-contact__eyebrow">{p.eyebrow}</p>
            <h2 className="k-h k-contact__h">{p.heading}</h2>
            <p className="k-contact__body">{p.body}</p>
            <div className="k-contact__links">
              <a href={`mailto:${p.email}`} className="k-contact__link k-contact__link--primary"><KIcon name="ph ph-envelope-simple" size={24} />{p.email}</a>
              <a href={p.partnerHref} className="k-contact__link"><KIcon name="ph ph-handshake" size={24} />{p.partnerLabel}</a>
            </div>
          </div>
          <div className="k-contact__card">
            <LeadFormFields submitLabel={p.submitLabel} messageLabel="Message" />
          </div>
        </div>
      </div>
    </section>
  );
}
export const kContactBlock: BlockDefinition = { type: 'kContact', schema: kContactSchema, Renderer: KContact };

// ── 13. Footer ────────────────────────────────────────────────────────────────
export const kFooterSchema = z.object({
  wordmark: z.string().default('Kathrefits AI'),
  tagline: z.string().default('Identity and access products for regulated enterprises — and the AI agents now inside them.'),
  address: z.string().default('Mumbai, India\nsales@cymmetri.com\nSupport: 24/7'),
  columns: z.array(z.object({ title: z.string(), links: z.array(footerLink).default([]) })).default([
    { title: 'What We Do', links: [
      { label: 'Cymmetri', href: 'https://cymmetri.com', newTab: true },
      { label: 'Bastion', href: 'https://thebastion.io', newTab: true },
      { label: 'Conflux ITDR', href: '/products', newTab: false },
      { label: 'IntelliAuth', href: 'https://intelliauth.io', newTab: true },
      { label: 'Cymmetri Learn', href: 'https://learn.cymmetri.com', newTab: true },
    ] },
    { title: 'Company', links: [
      { label: 'About us', href: '/about', newTab: false },
      { label: 'Careers', href: '/contact', newTab: false },
      { label: 'Partners', href: 'https://cymmetri.com/partner', newTab: true },
      { label: 'Contact us', href: '/contact', newTab: false },
    ] },
  ]),
  newsletterTitle: z.string().default('Newsletter'),
  newsletterBody: z.string().default('Identity and agent-governance notes, once a month.'),
  socials: z.array(z.object({ icon: z.string(), href: z.string(), newTab: z.boolean().default(true) })).default([
    { icon: 'ph ph-linkedin-logo', href: 'https://www.linkedin.com/company/cymmetri/', newTab: true },
    { icon: 'ph ph-x-logo', href: 'https://twitter.com/Cymmetri', newTab: true },
  ]),
  copyright: z.string().default('© 2026 Kathrefits AI Pvt Ltd. All rights reserved.'),
  legal: z.array(footerLink).default([
    { label: 'Privacy', href: 'https://cymmetri.com/privacy-policy', newTab: true },
    { label: 'Compliance', href: 'https://cymmetri.com/compliance', newTab: true },
    { label: 'Terms', href: 'https://thebastion.io/terms', newTab: true },
  ]),
});
function KFooter(p: z.infer<typeof kFooterSchema>) {
  return (
    <footer className="k-root k-footer">
      <div className="k-wrap">
        <div className="k-footgrid">
          <div className="k-foot-brand">
            <div className="k-foot-brand__row"><img className="k-site-logo k-site-logo--badge" src="/kathreftis/images/kathreftis-logo.png" alt={p.wordmark} /></div>
            <p>{p.tagline}</p>
            <p className="k-foot-addr">{p.address}</p>
          </div>
          {p.columns.map((col, i) => (
            <div className="k-fcol" key={i}>
              <input id={`k-foot-${i}`} className="k-facc" type="checkbox" aria-label={`Toggle ${col.title}`} />
              <label className="k-ftitle" htmlFor={`k-foot-${i}`}>{col.title}</label>
              <div className="k-flinks">
                {col.links.map((lk, j) => (<a key={j} href={lk.href} {...ext(lk.newTab)}>{lk.label}</a>))}
              </div>
            </div>
          ))}
          <div className="k-news">
            <div className="k-ftitle">{p.newsletterTitle}</div>
            <p>{p.newsletterBody}</p>
            <form className="k-news__form" method="post" action="#">
              <input className="k-input" placeholder="Email address" name="email" />
              <button type="submit" aria-label="Subscribe" className="k-btn k-news__send"><KIcon name="ph ph-paper-plane-tilt" size={16} /></button>
            </form>
            <div className="k-socials">
              {p.socials.map((s, i) => (<a key={i} href={s.href} {...ext(s.newTab)} aria-label={s.icon}><KIcon name={s.icon} size={18} /></a>))}
            </div>
          </div>
        </div>
      </div>
      <div className="k-footbar">
        <div className="k-wrap k-footbar__row">
          <span>{p.copyright}</span>
          <span className="k-footbar__legal">
            {p.legal.map((l, i) => (<a key={i} href={l.href} {...ext(l.newTab)}>{l.label}</a>))}
          </span>
        </div>
      </div>
    </footer>
  );
}
export const kFooterBlock: BlockDefinition = { type: 'kFooter', schema: kFooterSchema, Renderer: KFooter };

// ── 14. Page header band ──────────────────────────────────────────────────────
export const kPageHeaderSchema = z.object({
  kicker: z.string().default('Products'),
  title: z.string().default('Four Products. One Accountability Model.'),
  body: z.string().default(''),
});
function KPageHeader(p: z.infer<typeof kPageHeaderSchema>) {
  return (
    <section className="k-root k-band k-band--tint2 k-band--bb k-pt-58 k-pb-58">
      <div className="k-wrap">
        <p className="k-kicker">{p.kicker}</p>
        <h1 className="k-h k-pagehead__title">{p.title}</h1>
        {p.body ? <p className="k-body k-pagehead__body">{p.body}</p> : null}
      </div>
    </section>
  );
}
export const kPageHeaderBlock: BlockDefinition = { type: 'kPageHeader', schema: kPageHeaderSchema, Renderer: KPageHeader };

// ── 15. Products list (Products page) ─────────────────────────────────────────
export const kProductsListSchema = z.object({ products: z.array(productSchema).default(PRODUCTS) });
function KProductsList(p: z.infer<typeof kProductsListSchema>) {
  return (
    <section className="k-root k-band k-pt-56 k-pb-80">
      <div className="k-wrap k-plist">
        {p.products.map((pr, i) => (
          <article className="k-prow" key={i}>
            <div>
              <h3 className="k-h k-prow__name">{pr.name}</h3>
              <div className="k-prod__tag">{pr.tag}</div>
              <a href={pr.href} target="_blank" rel="noreferrer" className="k-btn k-btn--sm">Visit {pr.site}</a>
            </div>
            <div>
              <p className="k-prow__long">{pr.long}</p>
              <div className="k-detailgrid">
                {pr.detail.map((d, j) => (
                  <div className="k-detail" key={j}>
                    <KIcon name="ph ph-check" size={15} />
                    <div><div className="k-detail__t">{d.t}</div><div className="k-detail__b">{d.b}</div></div>
                  </div>
                ))}
              </div>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
export const kProductsListBlock: BlockDefinition = { type: 'kProductsList', schema: kProductsListSchema, Renderer: KProductsList };

// ── 16. About pillars ─────────────────────────────────────────────────────────
export const kAboutPillarsSchema = z.object({
  pillars: z.array(z.object({ icon: z.string(), title: z.string(), body: z.string() })).default([
    { icon: 'ph ph-flask', title: 'Deep-tech R&D', body: 'Research and engineering under one roof — identity protocols, behavioural risk models and agent governance.' },
    { icon: 'ph ph-gavel', title: 'Regulated By Default', body: 'We build for audit: evidence, separation of duties and residency are features, not afterthoughts.' },
    { icon: 'ph ph-handshake', title: 'Partner-Led Delivery', body: 'A channel of resellers and system integrators, enabled and certified through Cymmetri Learn.' },
  ]),
});
function KAboutPillars(p: z.infer<typeof kAboutPillarsSchema>) {
  return (
    <section className="k-root k-band k-pt-70 k-pb-60">
      <div className="k-wrap">
        <div className="k-grid3">
          {p.pillars.map((a, i) => (
            <div className="k-pillar" key={i}>
              <KIcon name={a.icon} size={32} className="k-pillar__icon" />
              <h3 className="k-h k-pillar__title">{a.title}</h3>
              <p className="k-body k-pillar__body">{a.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
export const kAboutPillarsBlock: BlockDefinition = { type: 'kAboutPillars', schema: kAboutPillarsSchema, Renderer: KAboutPillars };

// ── 17. How we work (About) ───────────────────────────────────────────────────
export const kHowWeWorkSchema = z.object({
  heading: z.string().default('How We Work'),
  paras: z.array(z.string()).default([
    "Small teams own a product outright — research, engineering, deployment and the customer's escalation path. Our engineers sit in the rollouts, which is why a platform migration here is measured in weeks rather than quarters.",
    'The AI-agent work in Bastion came out of that same loop: customers started handing credentials to autonomous systems, and nothing in the market treated an agent as an identity with an owner. So we built it.',
  ]),
  primaryLabel: z.string().default('Talk to us'), primaryHref: z.string().default('/contact'), primaryCalendly: z.boolean().default(false),
  secondaryLabel: z.string().default('Open roles'), secondaryHref: z.string().default('/contact'), secondaryCalendly: z.boolean().default(false),
  asideKicker: z.string().default('For investors & analysts'),
  asideBody: z.string().default('Four shipping products, enterprise references in Indian financial services, and a category bet on machine and agent identity. Briefing decks and reference calls on request.'),
  asideCtaLabel: z.string().default('Request a briefing'), asideCtaHref: z.string().default('mailto:sales@cymmetri.com'),
});
function KHowWeWork(p: z.infer<typeof kHowWeWorkSchema>) {
  return (
    <section className="k-root k-band k-band--tint1 k-band--bt k-pt-70 k-pb-70">
      <div className="k-wrap">
        <div className="k-two k-two--form">
          <div>
            <h2 className="k-h k-fs-30 k-mb-14">{p.heading}</h2>
            {p.paras.map((t, i) => (<p className="k-hww__p" key={i}>{t}</p>))}
            <div className="k-hww__cta">
              <a href={p.primaryCalendly ? '#calendly' : p.primaryHref} className="k-btn">{p.primaryLabel}</a>
              <a href={p.secondaryCalendly ? '#calendly' : p.secondaryHref} className="k-btn k-btn--navy">{p.secondaryLabel}</a>
            </div>
          </div>
          <aside className="k-aside">
            <p className="k-kicker">{p.asideKicker}</p>
            <p className="k-body">{p.asideBody}</p>
            <a href={p.asideCtaHref} className="k-btn k-btn--sm">{p.asideCtaLabel}</a>
          </aside>
        </div>
      </div>
    </section>
  );
}
export const kHowWeWorkBlock: BlockDefinition = { type: 'kHowWeWork', schema: kHowWeWorkSchema, Renderer: KHowWeWork };

// ── 18. Flagship strip (About closing) ────────────────────────────────────────
export const kFlagshipSchema = z.object({
  logoUrl: z.string().default(''),
  text: z.string().default('Cymmetri is our flagship brand and the platform most enterprise customers meet first. Bastion, IntelliAuth and Cymmetri Learn extend it.'),
});
function KFlagship(p: z.infer<typeof kFlagshipSchema>) {
  return (
    <section className="k-root k-band k-pt-60 k-pb-80">
      <div className="k-wrap">
        <div className="k-flagship">
          {p.logoUrl ? <img src={p.logoUrl} alt="Cymmetri" /> : <span className="k-flagship__name">Cymmetri</span>}
          <p className="k-body">{p.text}</p>
        </div>
      </div>
    </section>
  );
}
export const kFlagshipBlock: BlockDefinition = { type: 'kFlagship', schema: kFlagshipSchema, Renderer: KFlagship };

// ── 19a. Contact form + contacts aside ────────────────────────────────────────
export const kContactFormSchema = z.object({
  fields: fieldSchema,
  sendLabel: z.string().default('Send'),
  emailNote: z.string().default('Or email sales@cymmetri.com directly.'),
  contacts: z.array(z.object({ label: z.string(), value: z.string(), href: z.string() })).default([
    { label: 'Sales', value: 'sales@cymmetri.com', href: 'mailto:sales@cymmetri.com' },
    { label: 'Partners', value: 'Become a partner', href: 'https://cymmetri.com/partner' },
    { label: 'Press & analysts', value: 'sales@cymmetri.com', href: 'mailto:sales@cymmetri.com' },
  ]),
});
function KContactForm(p: z.infer<typeof kContactFormSchema>) {
  return (
    <section className="k-root k-band k-pt-56 k-pb-70">
      <div className="k-wrap">
        <div className="k-two k-two--form">
          <div className="k-formcard">
            <LeadFormFields submitLabel={p.sendLabel} messageLabel="Message" />
          </div>
          <aside className="k-contacts">
            {p.contacts.map((c, i) => (
              <div key={i}>
                <div className="k-contacts__label">{c.label}</div>
                <a href={c.href} className="k-contacts__value">{c.value}</a>
              </div>
            ))}
          </aside>
        </div>
      </div>
    </section>
  );
}
export const kContactFormBlock: BlockDefinition = { type: 'kContactForm', schema: kContactFormSchema, Renderer: KContactForm };

// ── 19b. Careers ──────────────────────────────────────────────────────────────
export const kCareersSchema = z.object({
  kicker: z.string().default('Careers'),
  heading: z.string().default('Build Identity Infrastructure That Banks Depend On.'),
  body: z.string().default('Mumbai and remote across India. Send work, not a cover letter.'),
  roles: z.array(z.object({ title: z.string(), team: z.string(), place: z.string(), href: z.string().default('mailto:sales@cymmetri.com') })).default([
    { title: 'Senior Backend Engineer — Identity', team: 'Cymmetri', place: 'Mumbai / Remote', href: 'mailto:sales@cymmetri.com' },
    { title: 'Security Researcher — Agent Governance', team: 'Bastion', place: 'Mumbai', href: 'mailto:sales@cymmetri.com' },
    { title: 'Solutions Architect — BFSI', team: 'Field', place: 'Mumbai', href: 'mailto:sales@cymmetri.com' },
    { title: 'Developer Advocate', team: 'IntelliAuth', place: 'Remote, India', href: 'mailto:sales@cymmetri.com' },
  ]),
});
function KCareers(p: z.infer<typeof kCareersSchema>) {
  return (
    <section className="k-root k-band k-band--tint1 k-band--bt k-pt-66 k-pb-76">
      <div className="k-wrap">
        <p className="k-kicker">{p.kicker}</p>
        <h2 className="k-h k-fs-32 k-mb-14">{p.heading}</h2>
        <p className="k-body">{p.body}</p>
        <div className="k-roles">
          {p.roles.map((r, i) => (
            <a className="k-role" href={r.href} key={i}>
              <span className="k-role__title">{r.title}</span>
              <span className="k-role__team">{r.team}</span>
              <span className="k-role__place">{r.place}</span>
              <KIcon name="ph ph-arrow-right" size={16} />
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}
export const kCareersBlock: BlockDefinition = { type: 'kCareers', schema: kCareersSchema, Renderer: KCareers };

// ── 19c. Legacy combined (kept for already-published pages) ───────────────────
export const kContactCareersSchema = z.object({
  fields: fieldSchema,
  sendLabel: z.string().default('Send'),
  emailNote: z.string().default('Or email sales@cymmetri.com directly.'),
  contacts: kContactFormSchema.shape.contacts,
  careersKicker: z.string().default('Careers'),
  careersHeading: z.string().default('Build Identity Infrastructure That Banks Depend On.'),
  careersBody: z.string().default('Mumbai and remote across India. Send work, not a cover letter.'),
  roles: kCareersSchema.shape.roles,
});
function KContactCareers(p: z.infer<typeof kContactCareersSchema>) {
  return (
    <>
      <KContactForm fields={p.fields} sendLabel={p.sendLabel} emailNote={p.emailNote} contacts={p.contacts} />
      <KCareers kicker={p.careersKicker} heading={p.careersHeading} body={p.careersBody} roles={p.roles} />
    </>
  );
}
export const kContactCareersBlock: BlockDefinition = { type: 'kContactCareers', schema: kContactCareersSchema, Renderer: KContactCareers };

// ── 20. Offices + map ─────────────────────────────────────────────────────────
export const kOfficesSchema = z.object({
  kicker: z.string().default('Our offices'),
  heading: z.string().default('Where To Find Us'),
  offices: z.array(z.object({
    label: z.string(),
    name: z.string().default('Kathrefits AI Pvt Ltd'),
    address: z.string(),
    mapQuery: z.string().default(''),
    directionsUrl: z.string().default(''),
  })).default([
    {
      label: 'Head Office',
      name: 'Kathrefits AI Pvt Ltd',
      address: '9th Floor, The Summit Business Park, Unit No. 905, Prakashwadi Rd, behind Guru Nanak Petrol Pump, opp. Cinemax Cinema, Gundavali, Andheri East, Mumbai, Maharashtra 400093',
      mapQuery: 'Kathrefits AI Pvt Ltd, Andheri East, Mumbai 400093',
      directionsUrl: 'https://www.google.com/maps/place/Kathreftis+AI+Pvt+Ltd/@19.1168825,72.8545836,17z/data=!3m1!5s0x3be7c8322215c5a5:0x2848c54159358db8!4m14!1m7!3m6!1s0x3be7b78d99878991:0x7083bfcf254f3ccb!2sKathreftis+AI+Pvt+Ltd!8m2!3d19.1164584!4d72.8572522!16s%2Fg%2F11ssflwqfk!3m5!1s0x3be7b78d99878991:0x7083bfcf254f3ccb!8m2!3d19.1164584!4d72.8572522!16s%2Fg%2F11ssflwqfk?entry=ttu',
    },
    {
      label: 'Development Center',
      name: 'Kathrefits AI Pvt Ltd',
      address: 'Zone-1, 8th Floor, The Coworking Company, Orange City Mall-1, 27, G/1 Sita Nagar Road, Jai Prakash Nagar, Somalwada, Nagpur, Maharashtra 440025',
      mapQuery: '',
      directionsUrl: '',
    },
  ]),
});
const mapSrc = (q: string) =>
  q.startsWith('https://www.google.com/maps/embed')
    ? q
    : `https://www.google.com/maps?q=${encodeURIComponent(q)}&output=embed`;
function KOffices(p: z.infer<typeof kOfficesSchema>) {
  return (
    <section className="k-root k-band k-band--tint1 k-band--bt k-pt-70 k-pb-80">
      <div className="k-wrap">
        <div className="k-center k-center--640">
          <p className="k-kicker">{p.kicker}</p>
          <h2 className="k-h k-fs-32">{p.heading}</h2>
        </div>
        <div className="k-plist">
          {p.offices.map((o, i) => (
            <div className="k-office" key={i}>
              <div className="k-office__body">
                <p className="k-kicker">{o.label}</p>
                <h3 className="k-h k-office__name">{o.name}</h3>
                <p className="k-body">{o.address}</p>
                <a
                  href={o.directionsUrl || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(o.mapQuery || o.address)}`}
                  target="_blank" rel="noopener noreferrer" className="k-office__dir"
                >Get directions <KIcon name="ph ph-arrow-up-right" size={14} /></a>
              </div>
              <iframe src={mapSrc(o.mapQuery || o.address)} title={`${o.label} map`} loading="lazy" referrerPolicy="no-referrer-when-downgrade" allowFullScreen />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
export const kOfficesBlock: BlockDefinition = { type: 'kOffices', schema: kOfficesSchema, Renderer: KOffices };

// Convenience list for registration.
export const kTeamSchema = z.object({
  eyebrow: z.string().default('Our leadership'),
  heading: z.string().default('Leadership team'),
  description: z.string().default(''),
  members: z
    .array(
      z.object({
        name: z.string(),
        designation: z.string().default(''),
        imageUrl: z.string().default(''),
        linkedin: z.string().default(''),
      }),
    )
    .default([]),
});
function KTeam(p: z.infer<typeof kTeamSchema>) {
  return (
    <section className="k-root k-band k-pt-70 k-pb-60 k-team">
      <div className="k-wrap">
        <span className="k-kicker">{p.eyebrow}</span>
        <h2 className="k-h k-fs-32">{p.heading}</h2>
        {p.description ? <p className="k-body k-team__desc">{p.description}</p> : null}
        <div className="k-team__grid">
          {p.members.map((m, i) => (
            <div className="k-member" key={i}>
              <KImage src={m.imageUrl} alt={m.name} className="k-member__photo" />
              <div className="k-member__body">
                <div>
                  <div className="k-member__name">{m.name}</div>
                  {m.designation ? <div className="k-member__role">{m.designation}</div> : null}
                </div>
                {m.linkedin ? (
                  <a className="k-member__linkedin" href={m.linkedin} target="_blank" rel="noopener noreferrer" aria-label={`${m.name} on LinkedIn`}>
                    <KIcon name="ph ph-linkedin-logo" size={20} />
                  </a>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
export const kTeamBlock: BlockDefinition = { type: 'kTeam', schema: kTeamSchema, Renderer: KTeam };

export const kBlocks: BlockDefinition[] = [
  kNavBlock, kHeroBlock, kServicesBlock, kCtaStripBlock, kStatementBlock, kAboutMissionBlock,
  kStatsBlock, kProductsBlock, kLogosBlock, kIndustriesBlock, kTestimonialsBlock, kContactBlock,
  kFooterBlock, kPageHeaderBlock, kProductsListBlock, kAboutPillarsBlock, kHowWeWorkBlock,
  kFlagshipBlock, kContactFormBlock, kCareersBlock, kContactCareersBlock, kOfficesBlock,
  kTeamBlock,
];
